import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/client';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { PageHeader } from '../components/PageHeader';
import { Plus, Trash2, Edit2, RefreshCw } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const CATEGORIES = [
  { value: 'company_assigned', label: 'Company Assigned' },
  { value: 'project_location', label: 'Location / Project ID' },
  { value: 'equipment_category', label: 'Equipment Category' },
  { value: 'equipment_type', label: 'Equipment Type' },
  { value: 'equipment_capacity', label: 'Equipment Capacity' },
  { value: 'status', label: 'Status' },
  { value: 'area', label: 'Area' },
  { value: 'nc_ii', label: 'NC II Certificate' },
];

const Dropdowns = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState('company_assigned');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: 'company_assigned', value: '' });
  const [editing, setEditing] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => setItems((await api.get('/dropdowns')).data), []);
  useEffect(() => { load(); }, [load]);

  const syncFromData = async () => {
    if (!window.confirm('Scan all equipment & operator records and auto-add any missing dropdown values?')) return;
    setSyncing(true);
    try {
      const r = await api.post('/dropdowns/sync-from-data');
      const total = r.data.total_added || 0;
      toast({ title: 'Sync complete', description: total === 0 ? 'No new options found.' : `Added ${total} new option${total !== 1 ? 's' : ''} across categories.` });
      load();
    } catch (e) {
      toast({ title: 'Sync failed', description: e.response?.data?.detail || 'Server error', variant: 'destructive' });
    } finally { setSyncing(false); }
  };

  const save = async () => {
    try {
      if (editing) await api.put(`/dropdowns/${editing.id}`, { value: form.value });
      else await api.post('/dropdowns', form);
      toast({ title: 'Saved' });
      setOpen(false); load();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail, variant: 'destructive' }); }
  };
  const remove = async (it) => { if (!window.confirm('Delete?')) return; await api.delete(`/dropdowns/${it.id}`); load(); };

  const filtered = items.filter((i) => i.category === activeCat);

  return (
    <div className="space-y-4">
      <PageHeader title="Add Company" description="Manage centralized dropdown options used across forms. Values are also auto-registered when equipment is added or uploaded."
        actions={<>
          <Button variant="outline" onClick={syncFromData} disabled={syncing}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${syncing ? 'animate-spin' : ''}`} /> Sync from data
          </Button>
          <Button onClick={() => { setEditing(null); setForm({ category: activeCat, value: '' }); setOpen(true); }} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]"><Plus className="h-4 w-4 mr-1.5" />Add Option</Button>
        </>} />

      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <button key={c.value} onClick={() => setActiveCat(c.value)} className={`px-3 py-1.5 text-sm rounded-md border ${activeCat === c.value ? 'bg-[hsl(217,91%,24%)] text-white border-[hsl(217,91%,24%)]' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>{c.label} <span className="ml-1 text-xs opacity-70">({items.filter((i) => i.category === c.value).length})</span></button>
        ))}
      </div>

      <Card><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-left text-slate-600"><tr><th className="p-3">Value</th><th className="p-3 text-right">Actions</th></tr></thead>
          <tbody className="divide-y">
            {filtered.length === 0 ? <tr><td colSpan={2} className="p-8 text-center text-slate-500">No options yet.</td></tr> :
              filtered.map((it) => (
                <tr key={it.id} className="table-row-hover">
                  <td className="p-3">{it.value}</td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(it); setForm({ category: it.category, value: it.value }); setOpen(true); }}><Edit2 className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(it)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit Option' : 'Add Option'}</DialogTitle><DialogDescription>Dropdown options apply across the system in real-time.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })} disabled={!!editing}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Value *</Label><Input value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]">{editing ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dropdowns;
